import { get, isEmpty, isFunction, keyBy } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLAN_PARAMS_RULES } from "utils/constants";
import { generateSQLCondition } from "utils/helpers/graphql";

const useInsurancePlans = ({
  setSelectedModules,
  insurancePlan,
  getOffers,
  project,
  offerWhere = [],
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedInsurancePlans, setSelectedInsurancePlans] = useState([]);
  const [buildCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [getGeneralCosts] = useLazyQuery(GENERAL_COSTS);

  const insurancePlanFilter = useMemo(() => {
    const issuanceDate = moment(get(project, "contract.issuanceDate")).startOf(
      "day",
    );
    const issuanceDateDifference = issuanceDate.diff(
      moment().startOf("day"),
      "days",
    );

    const adherentAge = moment(get(project, "contact.user.birthDate")).startOf(
      "year",
    );

    const age = moment().diff(adherentAge, "years");

    const [startKey, endKey] = project.ria
      ? [
          INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
        ]
      : [
          INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
        ];
    const socialRegime = get(project, "contact.socialRegime");

    const profession = get(project, "contact.profession");

    const sqlConditions = [
      generateSQLCondition(
        startKey,
        "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
        issuanceDateDifference,
      ),
      generateSQLCondition(
        endKey,
        `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
          moment(get(project, "contract.issuanceDate")).year() ===
          moment().year()
            ? "OR CAST(r.value AS NUMERIC) = 0"
            : ""
        }`,
        issuanceDateDifference,
      ),
      generateSQLCondition(
        INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
        "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
        age,
      ),
      generateSQLCondition(
        INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
        "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
        age,
      ),

      generateSQLCondition(
        INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES,
        `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
        `'${socialRegime}'`,
      ),
      generateSQLCondition(
        INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
        `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
        `'${profession}'`,
      ),
    ];

    return {
      where: { isStandalone: false },
      rules: sqlConditions.filter(Boolean),
      custom: {
        AND: [
          {
            OR: [
              { startDate: null },
              {
                startDate: issuanceDate,
                operation: "<=",
              },
            ],
          },
          {
            OR: [
              { endDate: null },
              {
                endDate: issuanceDate,
                operation: ">=",
              },
            ],
          },
        ],
      },
    };
  }, [project]);

  useEffect(() => {
    setSelectedInsurancePlans([]);
    setLoading(true);
  }, [insurancePlan?.id]);

  const fetchMainOffers = ({ offers }) => {
    const mainOffers = get(offers, "data", []);
    const [mainOffer] = mainOffers;
    if (!mainOffer) return setLoading(false);
    fetchAdditionalOffers(mainOffer.id);
  };

  const applyCosts = ({ costs, offerInsurancePlans }) => {
    const costItems = omitDeep(costs, "__typename").filter(
      ({ cost, insurancePlan: plan }) => cost && plan?.id,
    );

    if (isFunction(setSelectedModules)) {
      setSelectedModules((prev) => {
        const modules = get(prev, insurancePlan?.id, [])?.filter((module) =>
          costItems
            .map(({ insurancePlan: plan }) => plan?.id)
            .filter(Boolean)
            .includes(module?.insurancePlan?.id),
        );
        return {
          ...prev,
          [insurancePlan.id]: modules,
        };
      });
    }

    setSelectedInsurancePlans((prev) => {
      const newPlans = costItems.map((costItem) => ({
        ...costItem,
        weight: get(
          offerInsurancePlans.find(
            ({ insurancePlan: plan }) =>
              plan?.id === get(costItem, "insurancePlan.id", ""),
          ),
          "weight",
        ),
      }));
      const uniquePlans = newPlans.filter(
        (newPlan) => !prev.find(({ id }) => id === newPlan.id),
      );
      setLoading(false);
      return [...prev, ...uniquePlans];
    });
  };

  const fetchAdditionalOffers = (offerId) => {
    getOffers({
      fetchPolicy: "no-cache",
      variables: {
        where: {
          id: offerId,
          AND: [{ offerInsurancePlans: { main: false } }, ...offerWhere],
        },
      },
      onCompleted: ({ offers }) => {
        const offerInsurancePlans = get(offers, "data", []).flatMap(
          ({ offerInsurancePlans: plans }) => omitDeep(plans, "__typename"),
        );
        if (isEmpty(offerInsurancePlans)) return setLoading(false);
        const upsellPlanIds = offerInsurancePlans
          .map(({ insurancePlan: plan }) => plan?.id)
          .filter(Boolean);
        if (isEmpty(upsellPlanIds)) return setLoading(false);
        buildCosts({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlanFilter: {
                ...insurancePlanFilter,
                isIn: {
                  id: upsellPlanIds,
                },
              },
            },
          },
          onCompleted: ({ buildGeneralCosts }) => {
            if (!buildGeneralCosts?.length) {
              setLoading(false);
              return;
            }
            // Same flow as Acoris Products: build then re-query so nested
            // relations (supportingInformation) are fully loaded. Keep the
            // mutation payload as base so the list never disappears if the
            // re-query returns nothing.
            const builtCosts = buildGeneralCosts.filter(
              ({ cost, insurancePlan: plan }) => cost && plan?.id,
            );
            const ids = builtCosts.map(({ id }) => id).filter(Boolean);
            if (isEmpty(ids)) {
              setLoading(false);
              return;
            }

            const finishWithBuiltCosts = () =>
              applyCosts({
                costs: builtCosts,
                offerInsurancePlans,
              });

            getGeneralCosts({
              fetchPolicy: "no-cache",
              variables: {
                isIn: { id: ids },
              },
              onCompleted: ({ generalCosts }) => {
                const fetched = omitDeep(
                  get(generalCosts, "data", []),
                  "__typename",
                );
                if (isEmpty(fetched)) {
                  finishWithBuiltCosts();
                  return;
                }
                const fetchedById = keyBy(fetched, "id");
                const enrichedCosts = builtCosts.map((item) => {
                  const fromQuery = fetchedById[item.id];
                  if (!fromQuery) return item;
                  return {
                    ...omitDeep(item, "__typename"),
                    ...fromQuery,
                    insurancePlan: {
                      ...get(item, "insurancePlan", {}),
                      ...get(fromQuery, "insurancePlan", {}),
                    },
                  };
                });
                applyCosts({
                  costs: enrichedCosts,
                  offerInsurancePlans,
                });
              },
              onError: finishWithBuiltCosts,
            });
          },
        });
      },
    });
  };

  return {
    selectedInsurancePlans,
    fetchMainOffers,
    fetchAdditionalOffers,
    loading,
  };
};

export default useInsurancePlans;
